import type { FundraisingOption } from '@/types'
import { useI18n } from 'vue-i18n'

export const usePaymentOptions = () => {
  /**
   * Creates a payment option object.
   *
   * @param options - The options object containing the properties for the payment option.
   * @param options.id - The unique identifier for the payment option.
   * @param options.label - The display label for the payment option.
   * @param options.value - The value associated with the payment option.
   * @param options.hideOnMobile - A flag indicating whether the payment option should be hidden on mobile devices.
   * @param options.remaining - A flag indicating whether the option is remaining value for fundraisingDonate. Defaults to `false`.
   * @returns A `FundraisingOption` object containing the provided properties.
   */
  const createPaymentOption = ({
    id,
    label,
    value,
    hideOnMobile,
    remaining = false
  }: {
    id: number
    label: string
    value: number
    hideOnMobile: boolean
    remaining?: boolean
  }): FundraisingOption => ({
    id,
    label,
    value,
    hideOnMobile,
    remaining
  })

  const { t } = useI18n()

  const instalmentsOption: FundraisingOption[] = [
    createPaymentOption({
      id: 1,
      label: t('form.typeOfSupport.instalments.1'),
      value: 500,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: 2,
      label: t('form.typeOfSupport.instalments.2'),
      value: 200,
      hideOnMobile: false
    })
  ]

  const fundraisingOption: FundraisingOption[] = [
    createPaymentOption({
      id: 2,
      label: t('form.label.currency', { amount: 100 }),
      value: 100,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: 3,
      label: t('form.label.currency', { amount: 200 }),
      value: 200,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: 5,
      label: t('form.label.currency', { amount: 300 }),
      value: 300,
      hideOnMobile: true
    }),
    createPaymentOption({
      id: 4,
      label: t('form.label.currency', { amount: 500 }),
      value: 500,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: -1,
      label: t('form.label.otherAmount'),
      value: -1,
      hideOnMobile: false
    })
  ]

  const fundraisingDonateOption: FundraisingOption[] = [
    createPaymentOption({
      id: 1,
      label: t('form.label.currency', { amount: 50 }),
      value: 50,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: 2,
      label: t('form.label.currency', { amount: 100 }),
      value: 100,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: 3,
      label: t('form.label.currency', { amount: 200 }),
      value: 200,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: 4,
      label: t('form.label.currency', { amount: 500 }),
      value: 500,
      hideOnMobile: false
    }),
    createPaymentOption({
      id: -1,
      label: t('form.label.otherAmount'),
      value: -1,
      hideOnMobile: false
    })
  ]

  return {
    createPaymentOption,
    instalmentsOption,
    fundraisingOption,
    fundraisingDonateOption
  }
}
